import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error("Parolele nu coincid");
            return;
        }
        if (password.length < 6) {
            toast.error("Parola trebuie să aibă cel puțin 6 caractere");
            return;
        }
        setLoading(true);
        try {
            await API.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            setTimeout(() => navigate("/login"), 3000);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <div className="text-center px-6">
                    <p className="text-white/60 mb-4">Link invalid sau expirat.</p>
                    <Link to="/forgot-password" className="text-sm text-white/80 hover:text-white underline">
                        Solicită un link nou
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex relative overflow-hidden bg-[#0A0A0F]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {/* Background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-1/3 -left-1/4 w-[700px] h-[700px] rounded-full opacity-50 mix-blend-screen blur-3xl animate-liquid-1"
                    style={{ background: "radial-gradient(circle, rgba(96,165,250,0.25), rgba(96,165,250,0) 60%)" }} />
                <div className="absolute -bottom-1/3 -right-1/4 w-[800px] h-[800px] rounded-full opacity-60 mix-blend-screen blur-3xl animate-liquid-2"
                    style={{ background: "radial-gradient(circle, rgba(167,139,250,0.20), rgba(167,139,250,0) 65%)" }} />
                <div className="absolute inset-0 opacity-[0.025]" style={{
                    backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "44px 44px"
                }} />
            </div>

            <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 relative z-10">
                <div className="w-full max-w-[420px]">
                    <div className="flex items-center justify-center mb-8">
                        <img src="/logo-tom-v3.png" alt="TOM" className="h-14 w-auto object-contain drop-shadow-[0_0_30px_rgba(96,165,250,0.35)]" />
                    </div>

                    <div className="relative">
                        <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/[0.12] to-white/[0.02] opacity-80" />
                        <div className="relative bg-white/[0.03] backdrop-blur-2xl rounded-2xl border border-white/[0.08] p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">

                            {done ? (
                                <div className="text-center py-4">
                                    <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle2 size={22} className="text-emerald-400" />
                                    </div>
                                    <h2 className="text-[22px] font-bold text-white mb-2 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        Parolă actualizată
                                    </h2>
                                    <p className="text-sm text-white/50">Ești redirecționat la pagina de autentificare...</p>
                                </div>
                            ) : (
                                <>
                                    <h2 className="text-[26px] font-bold text-white mb-1.5 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                        Parolă nouă
                                    </h2>
                                    <p className="text-sm text-white/45 mb-7">Introdu noua parolă pentru contul tău.</p>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-medium text-white/55 mb-1.5 uppercase tracking-[0.12em]">Parolă nouă</label>
                                            <div className="relative">
                                                <input
                                                    type={showPass ? "text" : "password"}
                                                    value={password}
                                                    onChange={e => setPassword(e.target.value)}
                                                    placeholder="••••••••"
                                                    className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:bg-white/[0.06] focus:border-white/30 transition-all pr-10"
                                                    required
                                                    autoFocus
                                                    autoComplete="new-password"
                                                />
                                                <button type="button" onClick={() => setShowPass(!showPass)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors">
                                                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[11px] font-medium text-white/55 mb-1.5 uppercase tracking-[0.12em]">Confirmă parola</label>
                                            <input
                                                type={showPass ? "text" : "password"}
                                                value={confirm}
                                                onChange={e => setConfirm(e.target.value)}
                                                placeholder="••••••••"
                                                className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:bg-white/[0.06] focus:border-white/30 transition-all"
                                                required
                                                autoComplete="new-password"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full flex items-center justify-center gap-2 bg-white/[0.08] hover:bg-white/[0.14] text-white py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 mt-2 border border-white/15 hover:border-white/30 shadow-lg shadow-black/30"
                                        >
                                            {loading ? (
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : "Salvează parola nouă"}
                                        </button>
                                    </form>

                                    <div className="mt-6 text-center">
                                        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-white/45 hover:text-white/75 transition-colors">
                                            <ArrowLeft size={12} />
                                            Înapoi la autentificare
                                        </Link>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
